import { useState } from 'react'
import { Button, Dialog, DialogActions, DialogTitle, IconButton, Stack, Typography, useTheme } from '@mui/material'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined'
import List, { ListRenderer } from 'components/List'
import styled from '@emotion/styled'
import { User } from 'api/model'
import { useFetchOrganizationMembers } from 'hooks/use-organization'
import ProfilePhoto from 'components/ProfilePhoto'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { authenticatedUserAtom, toastAtom } from 'state'
import { orgsRemoveMember } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import AddUserModal from './AddUserModal'
import UserRole from './UserRole'
import UserDetailsSidebar from './UserDetailsSidebar'
import UserStatus from './UserStatus'
import { useUserIsAdmin } from 'hooks/use-authenticated-user'
import { useSearchParams } from 'react-router-dom'
import { useEffectOnce } from 'usehooks-ts'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import MobileOverlay from 'components/MobileOverlay/MobileOverlay'
import { getColorForDarkMode } from 'utils/darkMode'

// HoverRow will set it's class to `.isHovering` which we can use downstream to show the icons.
const HoverVisibleDiv = styled.div`
  visibility: hidden;

  .isHovering & {
    visibility: visible;
  }
`

const UserManagement = () => {
  const theme = useTheme()
  const isMobile = useCheckMobileScreen()

  const authenticatedUser = useRecoilValue(authenticatedUserAtom)
  const canEdit = useUserIsAdmin()

  const { activeOrganization, organizationMembers, setOrganizationMembers } = useFetchOrganizationMembers()

  const [isUserDetailsSidebarOpen, setIsUserDetailsSidebarOpen] = useState(false)
  const [userDetails, setUserDetails] = useState<User | null>(null)

  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false)
  const onAddUserClicked = () => {
    setIsAddUserModalOpen(true)
  }

  const [searchParams] = useSearchParams()
  useEffectOnce(() => {
    const approvalRequestEmail = searchParams.get('email')
    if (approvalRequestEmail) {
      setIsAddUserModalOpen(true)
    }
  })

  const setToast = useSetRecoilState(toastAtom)

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [pendingDeleteUser, setPendingDeleteUser] = useState<User | null>(null)
  const [isDeletingUser, setIsDeletingUser] = useState(false)

  const renderUserControls = (user: User) => {
    const canDelete = user.id !== authenticatedUser?.id
    const onDeleteClicked = () => {
      setPendingDeleteUser(user)
      setIsDeleteDialogOpen(true)
    }

    if (isMobile) {
      return (
        <>
          <IconButton color="secondary" disabled={!canEdit || !canDelete} onClick={onDeleteClicked}>
            <DeleteOutlinedIcon />
          </IconButton>

          <IconButton
            color="secondary"
            onClick={() => {
              setUserDetails(user)
              setIsUserDetailsSidebarOpen(true)
            }}>
            <ArrowForwardOutlinedIcon />
          </IconButton>
        </>
      )
    }

    return (
      <HoverVisibleDiv>
        <IconButton color="secondary" disabled={!canEdit || !canDelete} onClick={onDeleteClicked}>
          <DeleteOutlinedIcon />
        </IconButton>

        <IconButton
          color="secondary"
          onClick={() => {
            setUserDetails(user)
            setIsUserDetailsSidebarOpen(true)
          }}>
          <ArrowForwardOutlinedIcon />
        </IconButton>
      </HoverVisibleDiv>
    )
  }

  const listRenderer: ListRenderer = {
    rows: (organizationMembers ?? []).map((user) => {
      return {
        key: user.email,
        columnWidth: (column) => {
          switch (column) {
            case 0:
              return '32px'
            case 4:
              return '100px'
          }
        },
        columnEntry: (column) => {
          switch (column) {
            case 0:
              return (
                <div style={{ height: '32px' }}>
                  <ProfilePhoto src={user.picture} size="32px" name={user.name ?? user.email} />
                </div>
              )
            case 1:
              return <Typography variant="h6">{user.name ?? user.email}</Typography>
            case 2:
              return <UserRole user={user} activeOrganization={activeOrganization} />
            case 3:
              return <UserStatus organizationId={activeOrganization} user={user} canReinvitePendingUsers={canEdit} />
            default:
              return renderUserControls(user)
          }
        },
      }
    }),
    columnCount: 5,
    columnName: (column) => {
      switch (column) {
        case 1:
          return 'Name'
        case 2:
          return 'App access'
        case 3:
          return 'Status'
        default:
          return null
      }
    },
  }

  const onDeleteConfirmed = async () => {
    if (!pendingDeleteUser || !activeOrganization) {
      return
    }

    setIsDeletingUser(true)

    try {
      await orgsRemoveMember(activeOrganization, pendingDeleteUser.id)
      setOrganizationMembers((members) => {
        if (!members) {
          return null
        }

        return members.filter((user) => {
          return user.id !== pendingDeleteUser.id
        })
      })

      setToast({ title: 'User deleted successfully', severity: 'success' })
      setIsUserDetailsSidebarOpen(false)
    } catch (error) {
      setToast({ title: 'Delete user failed', severity: 'error' })
    } finally {
      setIsDeleteDialogOpen(false)
      setIsDeletingUser(false)
    }
  }

  const renderConfirmDeleteDialog = () => {
    return (
      <Dialog open={isDeleteDialogOpen}>
        <DialogTitle>
          Are you sure you want to delete {pendingDeleteUser?.name ?? pendingDeleteUser?.email}?
        </DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              setIsDeleteDialogOpen(false)
            }}
            disabled={isDeletingUser}
            sx={{
              color: getColorForDarkMode(theme),
            }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={onDeleteConfirmed} disabled={isDeletingUser}>
            Yes, delete
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Stack>
      <Stack direction={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems="top" mb={2}>
        <Typography variant="h4" mb={2}>
          User management
        </Typography>

        <Button
          variant="contained"
          color="primary"
          sx={{ mb: isMobile ? 2 : 0 }}
          onClick={onAddUserClicked}
          disabled={!canEdit}>
          Invite new user
        </Button>
      </Stack>

      <List renderer={listRenderer} isLoading={!organizationMembers} />

      {isMobile && isUserDetailsSidebarOpen ? (
        <MobileOverlay closeButtonColor="#222222" onClose={() => setIsUserDetailsSidebarOpen(false)}>
          <UserDetailsSidebar
            user={userDetails}
            organizationId={activeOrganization}
            open={isUserDetailsSidebarOpen}
            onClose={() => {
              setIsUserDetailsSidebarOpen(false)
            }}
            onDeleteClicked={() => {
              setPendingDeleteUser(userDetails)
              setIsDeleteDialogOpen(true)
            }}
            setOrganizationMembers={setOrganizationMembers}
          />
        </MobileOverlay>
      ) : (
        <UserDetailsSidebar
          user={userDetails}
          organizationId={activeOrganization}
          open={isUserDetailsSidebarOpen}
          onClose={() => {
            setIsUserDetailsSidebarOpen(false)
          }}
          onDeleteClicked={() => {
            setPendingDeleteUser(userDetails)
            setIsDeleteDialogOpen(true)
          }}
          setOrganizationMembers={setOrganizationMembers}
        />
      )}

      {isAddUserModalOpen && (
        <AddUserModal organizationId={activeOrganization} onClose={() => setIsAddUserModalOpen(false)} />
      )}

      {renderConfirmDeleteDialog()}
    </Stack>
  )
}

export default UserManagement
